import { useEffect, useState } from "react";
import { CalendarClock, HandCoins, Bell } from "lucide-react";
import { api, eur, fmtError } from "../lib/api";
import { toast } from "sonner";

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : "—";

export const StudioPayouts = ({ isAdmin }) => {
  const [data, setData] = useState(null);

  const load = () => {
    api.get("/studio/payouts").then((r) => setData(r.data)).catch(() => {});
  };
  useEffect(load, []);

  if (!data) return null;

  const markPaid = async (p) => {
    try {
      await api.post(`/admin/payouts/${p.payout_id}/paid`);
      toast.success(`Reversement de ${eur(p.amount)} marqué comme versé à ${p.artist_name}`);
      load();
    } catch (e) {
      toast.error(fmtError(e));
    }
  };

  return (
    <section className="border border-white/[0.08] bg-surface p-8" data-testid="studio-payouts">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="flex items-center gap-3 font-display text-2xl font-medium">
          <HandCoins className="h-5 w-5 text-amber-500" /> Reversements
        </h2>
        <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-zinc-500" data-testid="payout-cycle">
          <CalendarClock className="h-4 w-4 text-amber-500/70" />
          Automatique tous les {data.period_days} jours
          {data.next_run && <> · prochain relevé le {fmtDate(data.next_run)}</>}
        </p>
      </div>

      {!isAdmin && (
        <>
          {data.notifications?.filter((n) => !n.read || true).slice(0, 3).map((n) => (
            <div key={n.notification_id} className="mt-5 flex items-start gap-3 border border-amber-500/30 bg-amber-500/5 p-4" data-testid={`notif-${n.notification_id}`}>
              <Bell className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
              <p className="text-xs leading-relaxed text-amber-200/90">{n.message}</p>
            </div>
          ))}
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="border border-white/[0.08] p-5">
              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">En attente de reversement</p>
              <p className="mt-2 font-display text-3xl font-black text-amber-500" data-testid="payout-pending">
                {eur(data.pending_amount || 0)}
              </p>
            </div>
            <div className="border border-white/[0.08] p-5">
              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Calendrier</p>
              <p className="mt-2 text-sm leading-relaxed text-zinc-400">
                Le règlement de vos ventes est effectué automatiquement tous les {data.period_days} jours
                sur votre compte PayPal indiqué dans votre profil.
              </p>
            </div>
          </div>
          {data.payouts?.length > 0 && (
            <div className="mt-6 space-y-2" data-testid="payout-history">
              {data.payouts.map((p) => (
                <div key={p.payout_id} className="flex items-center justify-between border border-white/[0.06] p-4 text-sm">
                  <span className="text-zinc-400">{fmtDate(p.created_at)} — {p.sales_count} vente{p.sales_count > 1 ? "s" : ""}</span>
                  <span className="font-bold text-amber-400">{eur(p.amount)}</span>
                  <span className={`text-[10px] uppercase tracking-[0.18em] ${p.status === "paid" ? "text-emerald-400" : "text-amber-400"}`}>
                    {p.status === "paid" ? `Versé le ${fmtDate(p.paid_at)}` : "En préparation"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {isAdmin && (
        <>
          <div className="mt-6 space-y-2" data-testid="payout-artists">
            {data.pending.filter((a) => a.pending_amount > 0).length === 0 && (
              <p className="text-sm text-zinc-500">Aucun montant en attente de reversement.</p>
            )}
            {data.pending.filter((a) => a.pending_amount > 0).map((a) => (
              <div key={a.email} className="flex flex-wrap items-center gap-3 border border-amber-500/30 bg-amber-500/5 p-4 text-sm" data-testid={`payout-due-${a.email}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-display font-medium">{a.artist_name || a.email}</p>
                  <p className="text-[11px] text-zinc-500">
                    {a.pending_sales} vente{a.pending_sales > 1 ? "s" : ""} · PayPal : {a.paypal_email || "non renseigné"}
                  </p>
                </div>
                <span className="font-display text-xl font-black text-amber-500">{eur(a.pending_amount)}</span>
              </div>
            ))}
          </div>
          {data.payouts?.length > 0 && (
            <div className="mt-6 space-y-2" data-testid="payout-admin-history">
              <p className="text-xs uppercase tracking-[0.25em] text-zinc-500">Relevés générés</p>
              {data.payouts.map((p) => (
                <div key={p.payout_id} className="flex flex-wrap items-center gap-3 border border-white/[0.06] p-4 text-sm" data-testid={`payout-row-${p.payout_id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{p.artist_name} — {eur(p.amount)}</p>
                    <p className="text-[11px] text-zinc-500">{fmtDate(p.created_at)} · {p.sales_count} vente{p.sales_count > 1 ? "s" : ""}</p>
                  </div>
                  {p.status === "paid" ? (
                    <span className="text-[10px] uppercase tracking-[0.18em] text-emerald-400">Versé le {fmtDate(p.paid_at)}</span>
                  ) : (
                    <button
                      onClick={() => markPaid(p)}
                      data-testid={`payout-paid-${p.payout_id}`}
                      className="border border-emerald-500/50 px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-emerald-400 transition-colors hover:bg-emerald-500 hover:text-black"
                    >
                      Marquer versé
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
};
